import type { IntlShape } from 'react-intl';

import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixUser,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';

import { isHolidayCell, isJustifiedAbsenceForRestDayOverride } from './matrixCellDisplay';
import { isReplacementCell } from './isReplacementCell';
import { canCreateBlankPastDayAttendance } from './matrixRestDays';

const ABSENCE_KINDS = new Set<TWorkJourneyMatrixCellKind>([
  'justified_absence',
  'unjustified_absence',
  'vacation',
  'medical_leave',
  'paid_leave',
  'unpaid_leave',
  'prenatal',
  'postnatal',
  'other_absence',
]);

const formatDayMonth = (isoDate?: string): string => {
  if (!isoDate) return '';
  const ms = DateUtil.getTimestampMs(`${isoDate}T12:00:00`) ?? Date.parse(`${isoDate}T12:00:00`);
  if (!Number.isFinite(ms)) return isoDate;
  return DateUtil.toDateString(ms, 'DD/MM');
};

const formatPerson = (name?: string | null, idCard?: string | null): string => {
  const displayName = (name || '').trim();
  const card = (idCard || '').trim();
  if (displayName && card) return `${displayName} (${card})`;
  return displayName || card || '';
};

const kindReasonMessageId = (kind: TWorkJourneyMatrixCellKind): string | null => {
  switch (kind) {
    case 'justified_absence':
      return 'work.direction.glossary.item.AJ';
    case 'unjustified_absence':
      return 'work.direction.glossary.item.AI';
    case 'vacation':
      return 'work.direction.glossary.item.VAC';
    case 'medical_leave':
      return 'work.direction.glossary.item.LM';
    case 'paid_leave':
      return 'work.direction.glossary.item.PGR';
    case 'unpaid_leave':
      return 'work.direction.glossary.item.PSGR';
    case 'prenatal':
      return 'work.direction.glossary.item.PREN';
    case 'postnatal':
      return 'work.direction.glossary.item.POSTN';
    default:
      return null;
  }
};

export const getMatrixCellTooltipTitle = (
  intl: IntlShape,
  cell: IWorkJourneyMatrixCell | undefined,
  user: IWorkJourneyMatrixUser,
  options?: {
    restDates?: Set<string>;
    date?: string;
    daysOfWeek?: Array<string | number> | null;
    /** Cover-only row (replacement, not a journey member). */
    isCoverOnly?: boolean;
  },
): string | null => {
  // White / empty cell: the user was not scheduled on this journey that day.
  // Only past/today work blanks can create an AR — match the click handler.
  // Cover-only replacements never create attendance on blank days.
  if (!cell) {
    const person = formatPerson(user.displayName, user.idCard);
    const day = formatDayMonth(options?.date);
    if (!person || !day) return null;
    if (options?.isCoverOnly) {
      return intl.formatMessage(
        { id: 'workJourney.management.matrix.tooltip.replacementOnlyNoCreate' },
        { person, day },
      );
    }
    const date = options?.date;
    const canCreateAttendance =
      !!date && canCreateBlankPastDayAttendance(date, options?.daysOfWeek);
    return intl.formatMessage(
      {
        id: canCreateAttendance
          ? 'workJourney.management.matrix.tooltip.notScheduledCreate'
          : 'workJourney.management.matrix.tooltip.notScheduled',
      },
      { person, day },
    );
  }

  const fallbackName = cell.subjectDisplayName || user.displayName;
  const fallbackIdCard = cell.subjectIdCard || user.idCard;

  // Justified absences on journey rest days show as "D" — match the rest tooltip.
  if (options?.restDates?.has(cell.date) && isJustifiedAbsenceForRestDayOverride(cell)) {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const day = formatDayMonth(cell.date);
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.rest' },
      { person, day },
    );
  }

  if (isHolidayCell(cell)) {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const start = formatDayMonth(cell.exceptionStartDate || cell.date);
    const end = formatDayMonth(cell.exceptionEndDate || cell.date);
    if (!person) return null;
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.absence' },
      {
        person,
        start,
        end,
        reason: intl.formatMessage({ id: 'workJourney.management.matrix.tooltip.holiday' }),
      },
    );
  }

  // The row user leaves this journey that day to cover a shift somewhere else.
  if (cell.kind === 'replacement_out') {
    const cover = formatPerson(
      cell.replacementUserDisplayName || user.displayName,
      cell.replacementUserIdCard || user.idCard,
    );
    if (!cover) return null;

    const replaced = formatPerson(cell.replacedUserDisplayName, cell.replacedUserIdCard);
    const journey =
      (cell.replacementJourneyName || '').trim() ||
      intl.formatMessage({ id: 'workJourney.management.matrix.tooltip.otherJourney' });
    const day = formatDayMonth(cell.date);

    return intl.formatMessage(
      {
        id: replaced
          ? 'workJourney.management.matrix.tooltip.replacingElsewherePerson'
          : 'workJourney.management.matrix.tooltip.replacingElsewhere',
      },
      { cover, replaced, journey, day },
    );
  }

  // Once the coverage attendance record exists the chip becomes PRE/WORK, but
  // the day is still a replacement and the tooltip must say who covers whom.
  if (isReplacementCell(cell)) {
    const isReplacementChip = cell.kind === 'replacement';
    // The replaced employee is only known from the exception: falling back to
    // the row user would make the cover replace themselves.
    const replaced = formatPerson(cell.replacedUserDisplayName, cell.replacedUserIdCard);
    const cover = formatPerson(
      cell.replacementUserDisplayName || user.displayName,
      cell.replacementUserIdCard || user.idCard,
    );

    if (replaced && cover && replaced !== cover) {
      return intl.formatMessage(
        { id: 'workJourney.management.matrix.tooltip.replacement' },
        { replaced, cover },
      );
    }

    // Without the covered employee the day is still a cover; on a worked day
    // the regular copy is more informative than a nameless replacement line.
    if (isReplacementChip && cover) {
      return intl.formatMessage(
        { id: 'workJourney.management.matrix.tooltip.covering' },
        { cover, day: formatDayMonth(cell.date) },
      );
    }

    if (isReplacementChip) return null;
  }

  if (ABSENCE_KINDS.has(cell.kind)) {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const start = formatDayMonth(cell.exceptionStartDate || cell.date);
    const end = formatDayMonth(cell.exceptionEndDate || cell.date);
    const reasonKey = kindReasonMessageId(cell.kind);
    const reason =
      (cell.description || '').trim() ||
      (reasonKey ? intl.formatMessage({ id: reasonKey }) : '');
    if (!person) return null;
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.absence' },
      { person, start, end, reason },
    );
  }

  if (cell.kind === 'work') {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const day = formatDayMonth(cell.date);
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.present' },
      { person, day },
    );
  }

  if (cell.kind === 'planned') {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const day = formatDayMonth(cell.date);
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.planned' },
      { person, day },
    );
  }

  if (cell.kind === 'rest') {
    const person = formatPerson(fallbackName, fallbackIdCard);
    const day = formatDayMonth(cell.date);
    return intl.formatMessage(
      { id: 'workJourney.management.matrix.tooltip.rest' },
      { person, day },
    );
  }

  return null;
};
